import * as types from '../mutation-types';
import GoogleSheetsAPI from '../../api/googleSheets';
import { throwErrorMessage } from '../utils/api';

export const state = {
  connection: {
    configured: false,
    connected: false,
    email: null,
    connectedAt: null,
  },
  spreadsheets: [],
  sheets: {},
  columns: {},
  previewData: {},
  // ✅ Estado de erro para feedback visual
  error: {
    type: null, // 'PERMISSION_DENIED', 'API_DISABLED', 'NETWORK_ERROR', etc.
    message: null,
    details: null,
  },
  uiFlags: {
    isCheckingStatus: false,
    isConnecting: false,
    isDisconnecting: false,
    isFetchingSpreadsheets: false,
    isFetchingSheets: false,
    isFetchingColumns: false,
    isFetchingPreview: false,
    isSavingConfig: false,
  },
};

export const getters = {
  isConfigured($state) {
    return $state.connection.configured;
  },
  isConnected($state) {
    return $state.connection.connected;
  },
  getConnectionEmail($state) {
    return $state.connection.email;
  },
  getConnectionInfo($state) {
    return $state.connection;
  },
  getSpreadsheets($state) {
    return $state.spreadsheets || [];
  },
  getSheets: $state => spreadsheetId => {
    return $state.sheets[spreadsheetId] || [];
  },
  getColumns: $state => (spreadsheetId, sheetName) => {
    const key = `${spreadsheetId}_${sheetName}`;
    return $state.columns[key] || [];
  },
  getPreviewData: $state => (spreadsheetId, sheetName) => {
    const key = `${spreadsheetId}_${sheetName}`;
    return $state.previewData[key] || { rows: [], headers: [] };
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getError($state) {
    return $state.error;
  },
  hasError($state) {
    return $state.error.type !== null;
  },
};

export const actions = {
  checkConnectionStatus: async ({ commit }) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isCheckingStatus: true });
    try {
      const response = await GoogleSheetsAPI.getStatus();
      commit(types.default.SET_GOOGLE_SHEETS_CONNECTION, response.data);
      return response.data;
    } catch (error) {
      commit(types.default.SET_GOOGLE_SHEETS_CONNECTION, { connected: false });
      throwErrorMessage(error);
      return { connected: false };
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isCheckingStatus: false });
    }
  },

  initiateOAuth: async ({ commit }) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isConnecting: true });
    try {
      const response = await GoogleSheetsAPI.authorize();
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      }
      return response.data;
    } catch (error) {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isConnecting: false });
      throwErrorMessage(error);
      return { success: false };
    }
  },

  disconnect: async ({ commit, state: $state }) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isDisconnecting: true });
    try {
      await GoogleSheetsAPI.disconnect();
      commit(types.default.SET_GOOGLE_SHEETS_CONNECTION, {
        configured: $state.connection.configured,
        connected: false,
        email: null,
      });
      commit(types.default.CLEAR_GOOGLE_SHEETS_DATA);
      return { success: true };
    } catch (error) {
      throwErrorMessage(error);
      return { success: false };
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isDisconnecting: false });
    }
  },

  saveConfig: async ({ commit }, { clientId, clientSecret }) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isSavingConfig: true });
    try {
      const response = await GoogleSheetsAPI.saveConfig(clientId, clientSecret);
      if (response.data.success) {
        commit(types.default.SET_GOOGLE_SHEETS_CONNECTION, {
          configured: response.data.configured,
          connected: false,
          email: null,
        });
      }
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return { success: false };
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isSavingConfig: false });
    }
  },

  fetchSpreadsheets: async ({ commit }, limit = 50) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingSpreadsheets: true });
    // ✅ Limpar erro anterior
    commit(types.default.SET_GOOGLE_SHEETS_ERROR, { type: null, message: null, details: null });
    try {
      const response = await GoogleSheetsAPI.listSpreadsheets(limit);
      commit(types.default.SET_GOOGLE_SHEETS_SPREADSHEETS, response.data.spreadsheets);
      return response.data.spreadsheets;
    } catch (error) {
      // ✅ Analisar tipo de erro para feedback visual
      const errorMessage = error?.response?.data?.error || error?.message || String(error);
      let errorType = 'UNKNOWN_ERROR';
      let userMessage = 'Erro ao buscar planilhas. Tente novamente.';
      let details = null;

      if (errorMessage.includes('PERMISSION_DENIED') || errorMessage.includes('API has not been used')) {
        errorType = 'API_DISABLED';
        userMessage = 'A API do Google Drive não está habilitada no projeto. É necessário ativá-la no Google Cloud Console.';
        // Extrair URL do console se disponível
        const urlMatch = errorMessage.match(/(https:\/\/console\.developers\.google\.com[^\s]+)/);
        details = urlMatch ? urlMatch[1] : null;
      } else if (errorMessage.includes('invalid_grant') || errorMessage.includes('Token has been expired')) {
        errorType = 'TOKEN_EXPIRED';
        userMessage = 'Sua conexão com o Google expirou. Por favor, reconecte sua conta.';
      } else if (errorMessage.includes('UNAUTHENTICATED') || errorMessage.includes('401')) {
        errorType = 'UNAUTHENTICATED';
        userMessage = 'Não autenticado. Por favor, reconecte sua conta Google.';
      } else if (errorMessage.includes('Network Error') || errorMessage.includes('ECONNREFUSED')) {
        errorType = 'NETWORK_ERROR';
        userMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }

      commit(types.default.SET_GOOGLE_SHEETS_ERROR, {
        type: errorType,
        message: userMessage,
        details: details,
      });

      throwErrorMessage(error);
      return [];
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingSpreadsheets: false });
    }
  },

  fetchSheets: async ({ commit }, spreadsheetId) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingSheets: true });
    try {
      const response = await GoogleSheetsAPI.listSheets(spreadsheetId);
      commit(types.default.SET_GOOGLE_SHEETS_SHEETS, {
        spreadsheetId,
        sheets: response.data.sheets,
      });
      return response.data.sheets;
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingSheets: false });
    }
  },

  fetchColumns: async ({ commit }, { spreadsheetId, sheetName }) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingColumns: true });
    try {
      const response = await GoogleSheetsAPI.getColumns(spreadsheetId, sheetName);
      commit(types.default.SET_GOOGLE_SHEETS_COLUMNS, {
        spreadsheetId,
        sheetName,
        columns: response.data.columns,
      });
      return response.data.columns;
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingColumns: false });
    }
  },

  fetchPreviewData: async ({ commit }, { spreadsheetId, sheetName, limit = 10 }) => {
    commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingPreview: true });
    try {
      const response = await GoogleSheetsAPI.previewData(spreadsheetId, sheetName, limit);
      commit(types.default.SET_GOOGLE_SHEETS_PREVIEW, {
        spreadsheetId,
        sheetName,
        data: response.data,
      });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return { rows: [], headers: [] };
    } finally {
      commit(types.default.SET_GOOGLE_SHEETS_UI_FLAG, { isFetchingPreview: false });
    }
  },

  clearData: ({ commit }) => {
    commit(types.default.CLEAR_GOOGLE_SHEETS_DATA);
  },
};

export const mutations = {
  [types.default.SET_GOOGLE_SHEETS_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.default.SET_GOOGLE_SHEETS_CONNECTION]($state, connection) {
    $state.connection = {
      configured: connection.configured || false,
      connected: connection.connected || false,
      email: connection.email || null,
      connectedAt: connection.connected_at || null,
    };
  },
  [types.default.SET_GOOGLE_SHEETS_SPREADSHEETS]($state, spreadsheets) {
    $state.spreadsheets = spreadsheets || [];
  },
  [types.default.SET_GOOGLE_SHEETS_SHEETS]($state, { spreadsheetId, sheets }) {
    $state.sheets = {
      ...$state.sheets,
      [spreadsheetId]: sheets,
    };
  },
  [types.default.SET_GOOGLE_SHEETS_COLUMNS]($state, { spreadsheetId, sheetName, columns }) {
    const key = `${spreadsheetId}_${sheetName}`;
    $state.columns = {
      ...$state.columns,
      [key]: columns,
    };
  },
  [types.default.SET_GOOGLE_SHEETS_PREVIEW]($state, { spreadsheetId, sheetName, data }) {
    const key = `${spreadsheetId}_${sheetName}`;
    $state.previewData = {
      ...$state.previewData,
      [key]: data,
    };
  },
  [types.default.CLEAR_GOOGLE_SHEETS_DATA]($state) {
    $state.spreadsheets = [];
    $state.sheets = {};
    $state.columns = {};
    $state.previewData = {};
    $state.error = { type: null, message: null, details: null };
  },
  [types.default.SET_GOOGLE_SHEETS_ERROR]($state, error) {
    $state.error = {
      type: error.type || null,
      message: error.message || null,
      details: error.details || null,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
